import { type FC } from "react";
import { useSelector } from "react-redux";

import { formatScore, getHitAccessionVersion, getHitDatabaseName } from "@/app-utils/blast.ts";
import { type BlastHit, type Sequence } from "@/graphql.ts";
import { type IJobState } from "@/redux/modules/job.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { BLAST_RESULT_FIELDS, CELL_TYPE, VIEW } from "@/settings/settings.js";

import ButtonHeader from "../core/ButtonHeader.js";
import InMemoryTableWithSelectionModel from "../core/grid/InMemoryTableWithSelectionModel.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import BlastPageHeader from "./BlastPageHeader.js";

const BlastResultsPage: FC = () => {
  const { job } = useSelector<IAppState, IJobState>((state) => state.job);

  const blastResultsOverviewTableColumns: any[] = [
    {
      name: "Query Name",
      field: BLAST_RESULT_FIELDS.QUERY_ACCESSION,
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Query Description",
      field: BLAST_RESULT_FIELDS.QUERY_DESCRIPTION,
      cellType: CELL_TYPE.TEXT,
    },
    {
      name: "Best Hit Name",
      field: BLAST_RESULT_FIELDS.HIT_ACCESSION,
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Best Hit Database",
      field: BLAST_RESULT_FIELDS.HIT_DATABASE,
      cellType: CELL_TYPE.STRING,
    },
    {
      name: "Best Hit Description",
      field: BLAST_RESULT_FIELDS.HIT_DESCRIPTION,
      cellType: CELL_TYPE.TEXT,
    },
    {
      name: "Max Score",
      field: BLAST_RESULT_FIELDS.HIT_MAX_SCORE,
      cellType: CELL_TYPE.NUMBER,
    },
    {
      name: "Total Score",
      field: BLAST_RESULT_FIELDS.HIT_TOTAL_SCORE,
      cellType: CELL_TYPE.NUMBER,
    },
    {
      name: "Query Cov",
      field: BLAST_RESULT_FIELDS.HIT_QUERY_COVER,
      cellType: CELL_TYPE.NUMBER,
    },
    {
      name: "E-Value",
      field: BLAST_RESULT_FIELDS.HIT_E_VALUE,
      cellType: CELL_TYPE.NUMBER,
    },
    {
      name: "Identicals",
      field: BLAST_RESULT_FIELDS.HIT_IDENT,
      cellType: CELL_TYPE.NUMBER,
    },
  ];

  if (!job) {
    return null;
  }

  return (
    <>
      <BlastPageHeader
        job={job}
        subtitle={"Results Overview"}
        actionWidgets={[
          <ButtonHeader
            viewType={VIEW.BLAST_JOB_VIEW.type}
            hideBlastBtn={true}
            hideExportBtn={true}
            key={"blast-result-overview-page-tag-1"}
          />,
        ]}
      />
      <div className={styleLayout.page} style={{ backgroundColor: "white" }}>
        <InMemoryTableWithSelectionModel
          columns={blastResultsOverviewTableColumns}
          rowGridData={createRowData()}
          uniqueGridIdentifier={"blast-result-overview"}
          useSelectionModel={false}
        />
      </div>
    </>
  );

  function createRowData() {
    // duplicates each rowData in returned array
    return job.results?.map((jobResult) => {
      const query: Sequence = jobResult.query;
      const hits: BlastHit[] = jobResult.blastHits;

      const rowData: any = {
        id: jobResult.id, // this is the primary id of the result associated with this query
        jobId: job.id, // this is the id of the parent job (which may contain several query results)
      };

      rowData[BLAST_RESULT_FIELDS.QUERY_ACCESSION] = query
        ? `${query.accession}.${query.version}`
        : "[query sequence unavailable]";
      rowData[BLAST_RESULT_FIELDS.QUERY_DESCRIPTION] = query?.description || "";

      if (hits && hits.length > 0) {
        const firstHit: BlastHit = hits[0];

        rowData[BLAST_RESULT_FIELDS.HIT_DESCRIPTION] = firstHit.target == null ? "" : firstHit.target.description;
        rowData[BLAST_RESULT_FIELDS.HIT_ACCESSION] = getHitAccessionVersion(firstHit);
        rowData[BLAST_RESULT_FIELDS.HIT_DATABASE] = getHitDatabaseName(firstHit);
        rowData[BLAST_RESULT_FIELDS.HIT_MAX_SCORE] = formatScore(firstHit.bestHsp.score);
        rowData[BLAST_RESULT_FIELDS.HIT_TOTAL_SCORE] = formatScore(firstHit.totalScore);
        rowData[BLAST_RESULT_FIELDS.HIT_QUERY_COVER] = firstHit.bestHsp.queryCoverage;
        rowData[BLAST_RESULT_FIELDS.HIT_E_VALUE] = firstHit.bestHsp.eValue;
        rowData[BLAST_RESULT_FIELDS.HIT_IDENT] = firstHit.bestHsp.ident;
      } else {
        rowData[BLAST_RESULT_FIELDS.HIT_DESCRIPTION] = "No hits found";
      }

      return rowData;
    });
  }
};

export default BlastResultsPage;
